import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { barrattAPI, type SpendLeakageItem } from '../../lib/api';
import { getErrorMessage } from '../../hooks/useFetchData';

export type UploadFilter = 'all' | 'uploaded' | 'not_uploaded';

/** Server maximum per page; a 3,600-item report is eight parallel requests. */
export const LEAKAGE_ITEMS_PAGE_SIZE = 500;

const COMMODITY_CODE = /^[A-Z]{4}\d{3}$/;

/** Commodity code carried in the item description ("TIMB020: 12 receipts …"). */
export function itemCommodity(item: Pick<SpendLeakageItem, 'description'>): string {
  const head = (item.description || '').split(':')[0].trim().toUpperCase();
  return COMMODITY_CODE.test(head) ? head : '';
}

export interface CommodityOption {
  code: string;
  count: number;
}

export interface UseLeakageItemsResult {
  filter: UploadFilter;
  setFilter: (filter: UploadFilter) => void;
  /** Selected commodity code; '' = every commodity. */
  commodity: string;
  setCommodity: (code: string) => void;
  /** Distinct commodity codes across the whole report, with item counts. */
  commodities: CommodityOption[];
  /** Items matching the upload and commodity filters (search/sort/paging happen in the table). */
  items: SpendLeakageItem[];
  /** Count matching the current filters. */
  count: number;
  /** Every item in the report, unfiltered. */
  total: number;
  loading: boolean;
  error: string | null;
  /** Refetch everything — after add item / recalculate. */
  reload: () => void;
}

/**
 * Loads every item of a spend leakage report once (all pages in parallel)
 * so the table can search, filter by commodity, sort, page and export
 * without another round trip. The upload-status tabs and the commodity
 * select narrow the loaded set; a stale in-flight response never lands.
 *
 * @param reportId - Report whose items to load (null = nothing yet)
 * @returns Filtered items, the filters, and loading state
 */
export function useLeakageItems(reportId: number | null): UseLeakageItemsResult {
  const [filter, setFilter] = useState<UploadFilter>('all');
  const [commodity, setCommodity] = useState('');
  const [all, setAll] = useState<SpendLeakageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    if (!reportId) return;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    try {
      const first = await barrattAPI.getSpendLeakageItems(reportId, { page: 1, page_size: LEAKAGE_ITEMS_PAGE_SIZE });
      const pages = Math.ceil((first.data.count || 0) / LEAKAGE_ITEMS_PAGE_SIZE);
      const rest = await Promise.all(
        Array.from({ length: Math.max(pages - 1, 0) }, (_, i) =>
          barrattAPI.getSpendLeakageItems(reportId, { page: i + 2, page_size: LEAKAGE_ITEMS_PAGE_SIZE }),
        ),
      );
      if (id !== requestId.current) return;
      setAll([...first.data.results, ...rest.flatMap((r) => r.data.results)]);
    } catch (err: unknown) {
      if (id !== requestId.current) return;
      console.error('Failed to load leakage items:', err);
      setError(getErrorMessage(err, 'Failed to load leakage items'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    void load();
  }, [load]);

  const commodities = useMemo<CommodityOption[]>(() => {
    const counts = new Map<string, number>();
    all.forEach((item) => {
      const code = itemCommodity(item);
      if (code) counts.set(code, (counts.get(code) || 0) + 1);
    });
    return Array.from(counts, ([code, count]) => ({ code, count })).sort((a, b) => a.code.localeCompare(b.code));
  }, [all]);

  const items = useMemo(() => all.filter((item) => {
    if (filter === 'uploaded' && item.supplier_uploaded !== true) return false;
    if (filter === 'not_uploaded' && item.supplier_uploaded !== false) return false;
    if (commodity && itemCommodity(item) !== commodity) return false;
    return true;
  }), [all, filter, commodity]);

  return {
    filter,
    setFilter,
    commodity,
    setCommodity,
    commodities,
    items,
    count: items.length,
    total: all.length,
    loading,
    error,
    reload: () => void load(),
  };
}
